import React, { useEffect, useMemo, useState } from 'react';
import { BOAPI } from '../service/UsersService';

const MONTH_NAMES = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const DAY_HEADERS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];

const pad = (n) => String(n).padStart(2, '0');
const toKey = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

// טאב "ימי פעילות" - לוח שנה חודשי. ברירת המחדל: פעיל א'-ו', לא פעיל בשבת.
// לחיצה על יום פותחת עריכה: אפשר לסמן חג/יום חריג כלא-פעיל (או שבת חריגה כפעילה)
// ולהוסיף הערה. ביום לא פעיל האפליקציה חוסמת בקשת/מימוש קוד קופון ולא שולחת
// התראות קרבה. השרת שומר רק חריגים (AppActivityDays), ראו dbo.fnIsAppActiveOn
const ActivityDaysTab = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [days, setDays] = useState({}); // key(yyyy-mm-dd) -> { isActive, isOverride, note }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null); // yyyy-mm-dd
  const [noteDraft, setNoteDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstWeekday = new Date(year, month, 1).getDay(); // 0=ראשון

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const from = toKey(year, month, 1);
      const to = toKey(year, month, daysInMonth);
      const resp = await BOAPI.fetchActivityCalendar(from, to);
      if (resp?.[0]?.[0]?.code === 0) {
        const map = {};
        (resp[1] || []).forEach((r) => { map[r.day] = r; });
        setDays(map);
      } else {
        setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת לוח השנה');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelected(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const shiftMonth = (delta) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };

  const cells = useMemo(() => {
    const arr = [];
    for (let i = 0; i < firstWeekday; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(d);
    return arr;
  }, [firstWeekday, daysInMonth]);

  const selectDay = (d) => {
    const key = toKey(year, month, d);
    setSelected(key);
    setNoteDraft(days[key]?.note || '');
  };

  const save = async (isActive) => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      const resp = await BOAPI.setActivityDay(selected, isActive, noteDraft);
      if (resp?.[0]?.[0]?.code === 0) {
        const row = resp[1]?.[0];
        if (row) setDays((prev) => ({ ...prev, [row.day]: row }));
        if (isActive === null) setNoteDraft('');
      } else {
        setError(resp?.[0]?.[0]?.msg || 'השמירה נכשלה');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setSaving(false);
    }
  };

  const selectedInfo = selected ? days[selected] : null;
  const todayKey = toKey(today.getFullYear(), today.getMonth(), today.getDate());

  return (
    <div style={s.container}>
      <h2 style={s.title}>ימי פעילות</h2>
      <p style={s.hint}>
        כברירת מחדל האפליקציה פעילה בימים א׳-ו׳ ולא פעילה בשבת. ביום לא פעיל אי אפשר לבקש או לממש קוד קופון,
        ואין התראות קרבה לעסקים. לחצו על יום כדי לסמן חג / יום חריג, או להפוך שבת חריגה לפעילה.
      </p>

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeErr} onClick={() => setError('')}>✕</button>
        </div>
      )}

      <div style={s.layout}>
        <div style={s.calendarCard}>
          <div style={s.monthRow}>
            <button style={s.navBtn} onClick={() => shiftMonth(-1)}>›</button>
            <div style={s.monthLabel}>{MONTH_NAMES[month]} {year}</div>
            <button style={s.navBtn} onClick={() => shiftMonth(1)}>‹</button>
          </div>

          <div style={s.grid}>
            {DAY_HEADERS.map((h) => <div key={h} style={s.dayHeader}>{h}</div>)}
            {cells.map((d, i) => {
              if (d === null) return <div key={`e${i}`} />;
              const key = toKey(year, month, d);
              const info = days[key];
              const active = info ? !!info.isActive : true;
              const isOverride = !!info?.isOverride;
              const isSelected = selected === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => selectDay(d)}
                  style={{
                    ...s.dayCell,
                    ...(active ? s.dayActive : s.dayInactive),
                    ...(isOverride ? s.dayOverride : {}),
                    ...(isSelected ? s.daySelected : {}),
                    ...(key === todayKey ? s.dayToday : {}),
                  }}
                  title={info?.note || ''}
                >
                  <span style={s.dayNum}>{d}</span>
                  {info?.note ? <span style={s.dayNote}>{info.note}</span> : null}
                </button>
              );
            })}
          </div>
          {loading && <div style={s.loading}>טוען...</div>}

          <div style={s.legend}>
            <span style={s.legendItem}><i style={{ ...s.legendDot, background: '#e8f5e9', borderColor: '#a5d6a7' }} /> פעיל</span>
            <span style={s.legendItem}><i style={{ ...s.legendDot, background: '#ffebee', borderColor: '#ef9a9a' }} /> לא פעיל</span>
            <span style={s.legendItem}><i style={{ ...s.legendDot, background: '#fff', borderColor: '#1f6feb', borderWidth: 2 }} /> חריג ידני</span>
          </div>
        </div>

        <div style={s.sideCard}>
          {selected ? (
            <>
              <div style={s.sideTitle}>{selected.split('-').reverse().join('/')}</div>
              <div style={s.statusLine}>
                סטטוס: <b style={{ color: selectedInfo?.isActive ? '#2e7d32' : '#c62828' }}>{selectedInfo?.isActive ? 'פעיל' : 'לא פעיל'}</b>
                {selectedInfo?.isOverride ? ' (חריג ידני)' : ' (ברירת מחדל)'}
              </div>
              <label style={s.label}>הערה (למשל שם החג)</label>
              <input
                style={s.input}
                value={noteDraft}
                maxLength={100}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="לא חובה"
              />
              <div style={s.btnCol}>
                <button style={{ ...s.btn, background: '#c62828' }} disabled={saving} onClick={() => save(0)}>סמן כלא פעיל</button>
                <button style={{ ...s.btn, background: '#2e7d32' }} disabled={saving} onClick={() => save(1)}>סמן כפעיל</button>
                <button style={{ ...s.btn, background: '#8792a2' }} disabled={saving} onClick={() => save(null)}>אפס לברירת מחדל</button>
              </div>
            </>
          ) : (
            <div style={s.emptySide}>בחרו יום בלוח כדי לערוך אותו</div>
          )}
        </div>
      </div>
    </div>
  );
};

const s = {
  container:    { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  title:        { margin: '0 0 6px', color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  hint:         { color: '#8792a2', fontSize: 12.5, marginBottom: 18, lineHeight: 1.6, maxWidth: 720 },
  errorBanner:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr:     { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },
  layout:       { display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' },
  calendarCard: { border: '1px solid #e3e8ee', borderRadius: 12, padding: 16, background: '#fff', width: 460, maxWidth: '100%', boxSizing: 'border-box' },
  monthRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  monthLabel:   { fontSize: 17, fontWeight: 700, color: '#1a1f36' },
  navBtn:       { width: 34, height: 34, borderRadius: 8, border: '1px solid #e3e8ee', background: '#fff', cursor: 'pointer', fontSize: 18, lineHeight: 1 },
  grid:         { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 },
  dayHeader:    { textAlign: 'center', fontSize: 12, color: '#8792a2', fontWeight: 700, padding: '4px 0' },
  dayCell:      { minHeight: 58, borderRadius: 8, border: '1px solid transparent', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: '5px 2px', fontFamily: 'inherit' },
  dayActive:    { background: '#e8f5e9', borderColor: '#a5d6a7', color: '#1b5e20' },
  dayInactive:  { background: '#ffebee', borderColor: '#ef9a9a', color: '#b71c1c' },
  dayOverride:  { borderColor: '#1f6feb', borderWidth: 2 },
  daySelected:  { boxShadow: '0 0 0 3px rgba(31,111,235,0.35)' },
  dayToday:     { fontWeight: 800, textDecoration: 'underline' },
  dayNum:       { fontSize: 14, fontWeight: 700 },
  dayNote:      { fontSize: 9.5, marginTop: 2, lineHeight: 1.15, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  loading:      { textAlign: 'center', color: '#999', fontSize: 12, marginTop: 8 },
  legend:       { display: 'flex', gap: 16, marginTop: 14, fontSize: 12, color: '#525f7f', flexWrap: 'wrap' },
  legendItem:   { display: 'flex', alignItems: 'center', gap: 6 },
  legendDot:    { display: 'inline-block', width: 14, height: 14, borderRadius: 4, border: '1px solid' },
  sideCard:     { border: '1px solid #e3e8ee', borderRadius: 12, padding: 16, background: '#fff', width: 260, boxSizing: 'border-box' },
  sideTitle:    { fontSize: 18, fontWeight: 700, color: '#1a1f36', marginBottom: 6 },
  statusLine:   { fontSize: 13, color: '#525f7f', marginBottom: 14 },
  label:        { display: 'block', fontSize: 12.5, fontWeight: 700, color: '#1a1f36', marginBottom: 6 },
  input:        { width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 14, boxSizing: 'border-box', marginBottom: 14 },
  btnCol:       { display: 'flex', flexDirection: 'column', gap: 8 },
  btn:          { padding: '10px 14px', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  emptySide:    { color: '#8792a2', fontSize: 13, textAlign: 'center', padding: '20px 0' },
};

export default ActivityDaysTab;
